import {
  shouldRetryTrpcQuery,
  trpcQueryRetryDelayMs,
} from '@risksmart-app/components/src/utils/trpc/retryPolicy';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';

import { handleError } from './errorUtils';
import { getOrCreateQueryClient } from './queryClientRegistry';
import { DEDUPE_WINDOW_MS, shouldReportTrpcQueryError } from './trpcUtils';

/**
 * Exported for the dedupe-window test, which replays real mount timings
 * against this exact config rather than a hand-built client.
 */
export const makeQueryClient = () => {
  return new QueryClient({
    // tanstack-query swallows query errors unless a consumer reads isError —
    // mirror the Apollo errorLink (ApolloGraphqlProvider) so silent failures
    // still reach Sentry. Mutations are excluded: FormContext owns mutation
    // error display and reporting (RSP-6539).
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Suppression rules (and their tests) live in
        // trpcUtils.shouldReportTrpcQueryError.
        if (!shouldReportTrpcQueryError(error, query)) {
          return;
        }

        // Only the procedure path (queryKey[0]) is captured — the full key
        // embeds the procedure input (entity ids, filter selections), which
        // would widen what reaches Sentry beyond the Apollo errorLink's
        // operationName parity.
        handleError(error, {
          tags: { source: 'trpc-query' },
          extra: { procedurePath: query.queryKey[0] },
        });
      },
    }),
    defaultOptions: {
      queries: {
        // Stale-while-revalidate defaults (COI-451, retuned in COI-882):
        // - gcTime > 0 keeps the last response around so remounts render
        //   the previous data instantly while the refetch is in flight,
        //   instead of a blank loading state (the Apollo 'no-cache'
        //   emulation this replaces threw all data away on unmount).
        // The QueryClient is cleared on auth changes (TrpcProvider), so
        // cached data never crosses session/org boundaries.
        //
        // staleTime was 0, described as "we never trust cached data without
        // revalidating it". Measured in prod, that is not what it bought. A
        // single logical read fires 3-5 times per PAGE VIEW, because ~30
        // separate components consume the same hook (table field configs, form
        // field renderers, custom-attribute selectors, property filters) and
        // each mounts a beat apart as the tree renders. react-query dedupes
        // only what is concurrently IN FLIGHT, so with p95 latency ~500ms most
        // of those mounts miss the window and issue their own request.
        // `frontend.department.allTypes` — a parameterless query — went out 15
        // times in 56 seconds, twice under a second apart. That is not
        // revalidation, it is duplication.
        //
        // DEDUPE_WINDOW_MS collapses one page view's worth of mounts into a
        // single fetch while leaving navigation-level revalidation intact: it
        // is sized just above the largest gap measured WITHIN one such burst
        // (6.2s), and well under the gap between real navigations (~20s). Every
        // navigation still refetches; the same read stops being fetched five
        // times to render one screen.
        gcTime: 5 * 60 * 1000, // Keep unused query data for 5 minutes
        staleTime: DEDUPE_WINDOW_MS,
        refetchOnMount: true, // Refetch on mount once the data is stale
        refetchOnWindowFocus: false, // Don't refetch on window focus
        // Retry only what is transient at the browser's own hop — a 429, an
        // infra 5xx, a dead connection, auth still settling. A deterministic
        // failure is terminal here because the tRPC tier already retried its
        // own upstream. Classification and bounds live in shouldRetryTrpcQuery
        // (@risksmart-app/components/src/utils/trpc/retryPolicy), shared with
        // the third-party portal — the same hop, so the same rules (COI-882,
        // COI-159).
        retry: shouldRetryTrpcQuery,
        retryDelay: trpcQueryRetryDelayMs,
      },
      mutations: {
        // Never retried, at any layer. The V3 write path carries no idempotency
        // key, so a retry after a response that was lost in transit — the exact
        // case a retry is for — can duplicate the record it just created
        // (COI-159). react-query's own default is already 0 attempts; this is
        // explicit so it survives someone raising the global default.
        retry: false,
      },
    },
  });
};

export const getQueryClient = () => {
  return getOrCreateQueryClient(makeQueryClient);
};

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();
